import type {
	ExtractInjectionAPI,
	NextEditorPlugin,
	OptionalPlugin,
} from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { LocalIdPlugin } from '@atlaskit/editor-plugin-local-id';
import type { SelectionPlugin } from '@atlaskit/editor-plugin-selection';
import type { AIPlugin, FAILURE_REASON } from '@atlassian/editor-plugin-ai';

export type API = ExtractInjectionAPI<AiStreamingOrchestratorPlugin>;

export type StreamingCommandFactory<T extends StreamingCommand> = (
	api: API,
) => (args: T) => boolean;

// Source https://bitbucket.org/atlassian/conversational-ai-platform/src/main/modules/product/rovo/rovo-impl/src/main/resources/yaml/actions/internal/aifc_confluence_page_update.yaml#lines-67
export type ReplaceNodeCommand = { localId: string; type: 'replaceNode' };
export type InsertNodeBeforeCommand = { localId: string; type: 'insertNodeBefore' };
export type InsertNodeAfterCommand = { localId: string; type: 'insertNodeAfter' };
export type AppendNodeCommand =
	| {
			// streamingType is used to show the highlight or not while streaming. In the create flow we don't want to show the highlight
			// as it is distracting when updating large documents
			streamingType?: 'create' | 'update';
			type: 'appendNode';
	  }
	| {
			// streamingType is used to show the highlight or not while streaming. In the create flow we don't want to show the highlight
			// as it is distracting when updating large documents
			streamingType?: 'create' | 'update';
			type: 'appendNodeToEnd';
	  };

export type RemoveNodeCommand = { localId: string; type: 'removeNode' };
export type SetAttrsCommand = {
	attrs: Record<string, unknown>;
	localId: string;
	type: 'setAttrs';
};

export type WrapNodeCommand =
	| {
			localId: string;
			type: 'wrap';
			wrapperAttrs: Record<string, unknown>;
			wrapperType: string;
	  }
	| {
			localId: string;
			type: 'wrapNode';
			wrapperAttrs: Record<string, unknown>;
			wrapperType: string;
	  };

export type StreamableCommandWithValue =
	| ReplaceNodeCommand
	| InsertNodeBeforeCommand
	| InsertNodeAfterCommand
	| AppendNodeCommand;

// #region Public APIs
export type StreamableCommand = Exclude<StreamableCommandWithValue, 'value'>;

export type NonStreamableCommand = RemoveNodeCommand | WrapNodeCommand | SetAttrsCommand;

export type StreamingCommand = StreamableCommand | NonStreamableCommand;

export const allStreamingCommands = [
	'replaceNode',
	'removeNode',
	'insertNodeBefore',
	'insertNodeAfter',
	'appendNodeToEnd',
	'setAttrs',
	'wrap',
	'wrapNode',
	'appendNode',
] as const;

export type StreamingError = {
	apiName?: string;
	errorContent?: string;
	errorKey?: string;
	failureReason: FAILURE_REASON;
	statusCode?: number;
};

export type AiStreamingOrchestratorPluginOptions = {
	streamFlowPacerEnabled?: boolean;
};

export type AiStreamingOrchestratorPlugin = NextEditorPlugin<
	'aiStreamingOrchestrator',
	{
		actions: {
			/**
			 * Abort the streaming immediately and end the experience.
			 * Rather than finishing the current streaming, this will discard any in-progress streaming
			 * and end the experience. No further streaming can be done until a new action is initialized.
			 */
			abortSession: () => void;
			endSession: (error?: StreamingError) => Promise<void>;
			finishAction: (actionId: string, error?: StreamingError) => void;
			initAction: (
				command: StreamingCommand,
				options?: {
					hideLoadingScreen?: boolean;
				},
			) => string;
			startSession: (traceId?: string) => string;
			streamValue: (actionId: string, chunk: string) => void;
		};
		dependencies: [
			AIPlugin,
			OptionalPlugin<LocalIdPlugin>,
			OptionalPlugin<AnalyticsPlugin>,
			OptionalPlugin<SelectionPlugin>,
		];
		pluginConfiguration?: AiStreamingOrchestratorPluginOptions;
	}
>;
// #endregion
